import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { IoNotificationsOutline } from "react-icons/io5"
import Drawer from "../shared/Drawer"
import { fetchPayorDbStatus } from "../../utils/apiUtils"
import "./PayorDbStatus.scss"

const STALE_AFTER_MS = 3 * 60 * 1000

export function payorDbAlertLevel(item, now = Date.now()) {
  if (!item || item.status === "UNKNOWN") return "unknown"
  const ts = Date.parse(item.lastUpdatedTimestamp)
  if (Number.isFinite(ts) && now - ts > STALE_AFTER_MS) return "stale"
  if (String(item.status).toUpperCase() === "DOWN") return "down"
  if (String(item.status).toUpperCase() === "UP") return "up"
  return "unknown"
}

function formatWhen(iso) {
  if (!iso) return "—"
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return iso
  const delta = Date.now() - ms
  if (delta < 45_000) return "just now"
  if (delta < 90_000) return "1 minute ago"
  if (delta < 60 * 60 * 1000) return `${Math.round(delta / 60_000)} minutes ago`
  return new Date(ms).toLocaleString()
}

const LEVEL_SUMMARY = {
  down: "Oracle checks are failing. HRP reads will error.",
  stale: "Last check is older than 3 minutes.",
  unknown: "No status row has been written yet.",
  up: "Latest health check succeeded.",
}

function isAlertLevel(level) {
  return level === "down" || level === "stale"
}

function StatusMark({ showDot }) {
  return (
    <span className="payor-db-status__mark">
      <IoNotificationsOutline size="1.2rem" />
      {showDot && <span className="payor-db-status__dot" aria-hidden />}
    </span>
  )
}

function StatusItem({ item, expanded, onToggle }) {
  return (
    <div className={`sys-feed__item is-${item.level}${expanded ? " is-open" : ""}`}>
      <button type="button" className="sys-feed__row" onClick={onToggle}>
        <span className={`sys-feed__signal is-${item.level}`} aria-hidden />
        <span className="sys-feed__main">
          <span className="sys-feed__title-row">
            <span className="sys-feed__name">{item.title}</span>
            <span className={`sys-feed__pill is-${item.level}`}>{item.statusLabel}</span>
          </span>
          <span className="sys-feed__meta">
            <span>{item.summary}</span>
            {item.when && <span className="sys-feed__when">{item.when}</span>}
          </span>
        </span>
      </button>
      {expanded && (
        <dl className="sys-feed__details">
          {item.details.map((d) => (
            <div key={d.label} className="sys-feed__detail">
              <dt>{d.label}</dt>
              <dd>{d.value}</dd>
            </div>
          ))}
          {item.error && (
            <div className="sys-feed__detail is-error">
              <dt>Error</dt>
              <dd>{item.error}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  )
}

function FeedSection({ title, count, children, empty }) {
  return (
    <section className="sys-feed__section">
      <header className="sys-feed__section-head">
        <h3>{title}</h3>
        {count != null && <span>{count}</span>}
      </header>
      {children || <p className="sys-feed__empty">{empty}</p>}
    </section>
  )
}

const PayorDbStatus = () => {
  const [open, setOpen] = useState(false)
  const [expandedId, setExpandedId] = useState("payor-db")
  const query = useQuery({
    queryKey: ["payor-db-status"],
    queryFn: fetchPayorDbStatus,
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: false,
  })
  const item = query.data && !query.data.errorType ? query.data : null
  const level = useMemo(() => payorDbAlertLevel(item), [item, query.dataUpdatedAt])

  const statusItems = useMemo(() => {
    if (query.isLoading) return []
    if (query.isError) {
      return [{
        id: "payor-db",
        title: "Payor database",
        level: "down",
        statusLabel: "ERROR",
        summary: "Could not load status.",
        when: null,
        details: [],
        error: null,
      }]
    }
    const details = [
      { label: "Last check", value: formatWhen(item?.lastUpdatedTimestamp) },
      item?.latencyMs != null && { label: "Latency", value: `${item.latencyMs} ms` },
      item?.database && { label: "Database", value: item.database },
      item?.schema && { label: "Schema", value: item.schema },
      item?.host && { label: "Host", value: item.host },
      item?.hostname && { label: "Checker", value: item.hostname },
    ].filter(Boolean)
    return [{
      id: "payor-db",
      title: "Payor database",
      level,
      statusLabel: item?.status || "UNKNOWN",
      summary: LEVEL_SUMMARY[level],
      when: formatWhen(item?.lastUpdatedTimestamp),
      details,
      error: item?.error || item?.message || null,
    }]
  }, [item, level, query.isError, query.isLoading])

  const notifications = []
  const alertCount = [...statusItems, ...notifications].filter((i) => isAlertLevel(i.level)).length
  const showDot = alertCount > 0

  return (
    <>
      <button
        type="button"
        className={`navbar-icon-btn payor-db-status__btn${open ? " is-open" : ""}`}
        onClick={() => setOpen(true)}
        title={alertCount ? `${alertCount} system alert${alertCount === 1 ? "" : "s"}` : "System status"}
        aria-label="System status"
        aria-expanded={open}
      >
        <StatusMark showDot={showDot} />
      </button>

      <Drawer
        open={open}
        onOpenChange={setOpen}
        title="System status"
        description="Live system checks and notifications."
        contentClassName="sys-status-drawer"
        bodyClassName="sys-feed"
      >
        <FeedSection title="Status" count={statusItems.length || null}>
          {query.isLoading
            ? <p className="sys-feed__empty">Checking…</p>
            : statusItems.map((row) => (
              <StatusItem
                key={row.id}
                item={row}
                expanded={expandedId === row.id}
                onToggle={() => setExpandedId((id) => (id === row.id ? null : row.id))}
              />
            ))}
        </FeedSection>
        <FeedSection
          title="Notifications"
          count={notifications.length}
          empty="No notifications"
        />
      </Drawer>
    </>
  )
}

export default PayorDbStatus
